import type { components } from "@appretio/api";
import { useRetainedRef, useSerialAsync } from "@appretio/shared";
import { useMutation } from "@tanstack/react-query";
import type { GridApi } from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import {
  MutableRefObject,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import {
  type CubeWindow,
  cutPastWindowEnd,
  extractTransactionalGridDataByProducer
} from "../../components/cubeDataTable/utils/columnUtils";
import {
  extractIntakeRowsFromGrid,
  INITIAL_INTAKE_SOURCE,
  injectIntakeIntoGrid,
  INTAKE_SOURCES,
  type IntakeCache,
  type IntakeCaches,
  intakeDataToCaches,
  intakeRowsToCache,
  type IntakeSource,
  isEditableIntakeSource,
  overMainSource
} from "../../components/cubeDataTable/utils/intakeUtils";
import { type ForecastTableControlValues } from "../../components/tableControls/forecastTableControlValues";
import {
  recomputeIntakeLocally,
  type TemporalGridRow
} from "../../utils/weekly/temporalGridAdapter";
import { type CubePage, pageScope } from "../useCubeSelectionState";
import { type ForecastGridDataOutput } from "../useFetchForecastData";
import { type ForecastMetaDataType } from "../useFetchForecastMetaData";

/** Options for {@link useWorkspaceIntake}. */
interface UseWorkspaceIntakeOptions {
  /**
   * The page whose intake cache this is; retained in its scope. Absent on a
   * grid that is not a page, whose cache then lives and dies with its mount.
   */
  page?: CubePage;
  /** Ref to the AG Grid instance. */
  gridRef: RefObject<AgGridReact | null>;
  /** Metadata containing year mapping for grid-data extraction. */
  metaData: ForecastMetaDataType | undefined;
  /** Current table control values (reads ``intake`` mode). */
  tableControls: ForecastTableControlValues;
  /** The most recent forecast data from the server. */
  forecastData: ForecastGridDataOutput | null;
  /** The applied context's markdown status: it signs the planned-markdown EOP correction. */
  markdownStatus?: string[] | null;
  /**
   * The window of the cube the grid saves to, and the producer whose rows it
   * holds: that producer's recompute is cut at the window, so the toggle can't
   * refill cells the cube never had. Absent on a grid with no cube.
   */
  saveCubeWindow?: CubeWindow;
  saveProducer?: string;
  /** Grid-state retention's capture for a transaction's rows (``GridStateRetention.rowSink``); absent with no retention. */
  rowSink?: () => (rows: TemporalGridRow[]) => void;
}

/** Return shape of {@link useWorkspaceIntake}. */
interface WorkspaceIntakeResult {
  /**
   * True from the moment a toggle is queued until its swap lands:
   * the grid-row swap and the recompute, the span during which the
   * UI is unresponsive.
   */
  intakePending: boolean;
  /** Ref holding one intake cache per source not on the grid. */
  intakeCacheRef: MutableRefObject<IntakeCaches>;
  /** The sources with rows to swap in; a tab outside it is disabled. */
  availableIntakeSources: IntakeSource[];
  /**
   * The source whose rows the grid shows. It follows the grid, not the
   * control: after a failed swap the grid rolls back while the control keeps
   * the source it asked for, so a save must read this.
   */
  gridSource: IntakeSource;
}

/** The sources whose cache holds rows. */
function availableSources(caches: IntakeCaches): IntakeSource[] {
  return INTAKE_SOURCES.filter((source) => caches[source]?.size);
}

// Resolve after two animation frames so React's pending re-render and the
// subsequent browser paint land before the caller resumes with blocking
// synchronous work. One rAF queues the callback for the next frame; the
// second ensures that frame's paint has actually flushed.
function paintYield(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/** What a swap needs from the hook: the recalc mutation and the rows' destination. */
interface SwapDeps {
  yearMapping: Record<string, number> | undefined;
  recalculate: ReturnType<typeof useIntakeRecompute>["mutateAsync"];
  captureRows: (rows: TemporalGridRow[]) => void;
}

/**
 * The client-side recompute after a swap, per producer (`recalc_intake_kpis`),
 * as a mutation so a failure toasts.
 */
function useIntakeRecompute({
  metaData,
  forecastData,
  markdownStatus,
  saveCubeWindow,
  saveProducer
}: Pick<
  UseWorkspaceIntakeOptions,
  | "metaData"
  | "forecastData"
  | "markdownStatus"
  | "saveCubeWindow"
  | "saveProducer"
>) {
  return useMutation({
    mutationFn: async (
      rowsByProducer: Record<
        string,
        components["schemas"]["TransactionalGridData"][]
      >
    ): Promise<{ update: TemporalGridRow[] }> => ({
      update: Object.entries(rowsByProducer).flatMap(function recompute([
        producer,
        rows
      ]) {
        const cut =
          producer === saveProducer
            ? cutPastWindowEnd(rows, metaData, saveCubeWindow)
            : rows;
        return recomputeIntakeLocally(
          cut,
          producer,
          metaData,
          markdownStatus,
          forecastData?.forecast_week_from
        ).update;
      })
    }),
    meta: { errorMessage: "Failed to compute intake changes" }
  });
}

/**
 * Swaps ``cache``'s intake rows into the grid and recalculates what depends
 * on them. Every row written goes to retention, so a remount shows the mode
 * as it was on screen. On failure the grid rolls back to ``fallback`` and
 * the error propagates; the mutation's ``meta.errorMessage`` surfaces the
 * toast.
 */
async function swapIntake(
  api: GridApi,
  cache: IntakeCache,
  fallback: IntakeCache,
  { yearMapping, recalculate, captureRows }: SwapDeps
) {
  captureRows(injectIntakeIntoGrid(api, cache));
  try {
    const result = await recalculate(
      extractTransactionalGridDataByProducer(api, yearMapping)
    );
    api.applyTransaction(result);
    captureRows(result.update);
  } catch (error) {
    captureRows(injectIntakeIntoGrid(api, fallback));
    throw error;
  } finally {
    // Index / per-SQM / SH% cells read another row: see `applyAndRefresh`.
    api.refreshCells({ force: true, suppressFlash: true });
  }
}

/** The refs the hydration and the toggle write: the caches and the source the grid shows. */
interface IntakeRefs {
  caches: MutableRefObject<IntakeCaches>;
  gridSource: MutableRefObject<IntakeSource>;
  /** Mirrors ``gridSource`` into the hook's state. */
  setGridSource: (source: IntakeSource) => void;
}

/**
 * Records ``source`` as the one the grid shows, in the ref and the state at
 * once. The state must not lag the ref: a save reads the state, and a save
 * mid-swap that read the outgoing source would cache the incoming rows under
 * it and send them as that source.
 */
function showSource(refs: IntakeRefs, source: IntakeSource) {
  refs.gridSource.current = source;
  refs.setGridSource(source);
}

/** The hook's bound {@link swapIntake}. */
type SwapFn = (
  api: GridApi,
  cache: IntakeCache,
  fallback: IntakeCache
) => Promise<void>;

/**
 * Rebuilds the caches from a payload. An editable source's cache holds the
 * planner's edits and survives a refetch (a restate); every other source
 * refreshes from the payload. On an overlay source the payload's rows are the
 * main source's, so the overlay is swapped back in.
 */
function hydrateIntakeCaches(
  data: ForecastGridDataOutput,
  source: IntakeSource,
  refs: IntakeRefs,
  api: GridApi | undefined,
  swap: SwapFn
): Promise<void> | undefined {
  const mainSource = data.main_intake_source;
  const edited = isEditableIntakeSource(source)
    ? refs.caches.current[source]
    : undefined;
  const caches = intakeDataToCaches(data.intake_data);
  if (edited) caches[source] = edited;
  refs.caches.current = caches;
  showSource(refs, mainSource);

  const overlay = caches[source];
  if (source === mainSource || !overlay || !api) return undefined;
  // The main source's rows come from the payload, not the grid, which may
  // still hold the rows the fetch replaced.
  const main = intakeRowsToCache(data.fiscal_grid_data);
  caches[mainSource] = main;
  showSource(refs, source);
  return swap(api, overMainSource(caches, overlay, mainSource), main).catch(
    () => {
      // Rolled back to the main source's rows; the control stays on the
      // overlay, and the next toggle to it retries. A later payload owns the
      // refs by now and has already set them.
      if (refs.caches.current !== caches) return;
      delete caches[mainSource];
      showSource(refs, mainSource);
    }
  );
}

/**
 * Swaps ``target``'s cached rows onto the grid, first caching the outgoing
 * source's rows from the grid. On failure the grid source rolls back and the
 * error propagates.
 */
async function toggleGridIntake(
  api: GridApi,
  target: IntakeSource,
  mainSource: IntakeSource,
  refs: IntakeRefs,
  swap: SwapFn
) {
  const fromSource = refs.gridSource.current;
  if (fromSource === target) return;
  // The rows on screen are the outgoing source's, edits included.
  const caches = refs.caches.current;
  caches[fromSource] = extractIntakeRowsFromGrid(api);
  const next = caches[target];
  if (!next?.size) return;
  showSource(refs, target);
  try {
    await swap(
      api,
      overMainSource(caches, next, mainSource),
      caches[fromSource]
    );
  } catch (error) {
    showSource(refs, fromSource);
    throw error;
  }
}

/**
 * Starts a toggle each time the control's intake changes, unless the grid
 * already shows that source with no swap running — as when the control
 * follows a payload onto the payload's own source.
 */
function useIntakeToggleEffect(
  intake: IntakeSource,
  gridSourceRef: Readonly<{ current: IntakeSource }>,
  toggleRunningRef: Readonly<{ current: boolean }>,
  {
    trigger,
    setIsToggling
  }: {
    /** Serialised toggle (``useSerialAsync``). */
    trigger: (mode: IntakeSource) => void;
    setIsToggling: (toggling: boolean) => void;
  }
) {
  // Holds the previous intake mode so the toggle effect can no-op when
  // the value is unchanged — including on a remount, which seeds it with
  // the retained mode. Lifetime invariant: ``useForecastWorkspace`` is
  // mounted once per producer/cube — switching producers is expected to
  // remount this hook, which resets the ref. If a future caller reuses
  // this hook across producers, reset the ref on ``producer``/``cubeIds``
  // change to avoid carrying a stale mode.
  const prevIntakeRef = useRef(intake);
  useEffect(() => {
    if (intake === prevIntakeRef.current) return;
    prevIntakeRef.current = intake;
    if (intake === gridSourceRef.current && !toggleRunningRef.current) return;
    // Flip ``isToggling`` before kicking the handler. The handler's
    // first ``await paintYield()`` will then let React paint this
    // state (disabled tab + spinner) before the sync grid work runs.
    setIsToggling(true);
    trigger(intake);
  }, [intake, gridSourceRef, toggleRunningRef, trigger, setIsToggling]);
}

/**
 * Manages the intake caches, hydration on data load, and the toggle between
 * intake sources. The toggle effect watches ``tableControls.intake`` and
 * swaps grid rows accordingly.
 *
 * Race guard: rapid toggles are serialised via {@link useSerialAsync}, so
 * in a Confirmed → Planned → Confirmed click sequence each swap reads the
 * grid the previous one left.
 */
export function useWorkspaceIntake({
  page,
  gridRef,
  metaData,
  tableControls,
  forecastData,
  rowSink,
  ...cube
}: UseWorkspaceIntakeOptions): WorkspaceIntakeResult {
  const recompute = useIntakeRecompute({ metaData, forecastData, ...cube });

  // Local "toggle in progress" state. We can't rely on
  // ``recompute.isPending`` alone: the mutation only starts
  // *after* the synchronous grid-row extract/inject work finishes,
  // which on large grids is the part that actually freezes the UI.
  // This flag flips true *before* that sync work begins so the
  // disabled tab + spinner paint first (see the effect below and
  // ``paintYield`` at the top of ``handleIntakeToggle``).
  const [isToggling, setIsToggling] = useState(false);

  const scope = page && pageScope(page);
  // Retained with the grid data it was snapshotted from and the mode that
  // selects among its entries: a per-mount cache would leave a retained
  // "Planned" mode labelling the payload's confirmed rows.
  const intakeCacheRef = useRetainedRef<IntakeCaches>(
    scope,
    "intakeCaches",
    {}
  );
  // The source whose rows the grid shows right now. Tracks the grid, not the
  // control: a queued toggle reads it after the previous one has landed, and
  // a remount finds the rows retention restored.
  const gridSourceRef = useRetainedRef<IntakeSource>(
    scope,
    "intakeGridSource",
    INITIAL_INTAKE_SOURCE
  );
  // The payload the cache was last hydrated from. Hydration is per payload,
  // not per mount: a remount serves the retained payload again, and the
  // grid — its rows patched from retention — already shows the mode the
  // planner left, edits included.
  const hydratedForRef = useRetainedRef<ForecastGridDataOutput | null>(
    scope,
    "intakeHydratedFor",
    null
  );

  const { mutateAsync: recalculate } = recompute;
  const yearMapping = metaData?.yearMapping;
  const swap = useCallback(
    (api: GridApi, cache: IntakeCache, fallback: IntakeCache) =>
      // The sink is taken per swap: it is bound to the load it captures for.
      swapIntake(api, cache, fallback, {
        yearMapping,
        recalculate,
        captureRows: rowSink ? rowSink() : () => {}
      }),
    [yearMapping, recalculate, rowSink]
  );
  const [availableIntakeSources, setAvailableIntakeSources] = useState(() =>
    availableSources(intakeCacheRef.current)
  );
  // ``gridSourceRef`` mirrored into state: set with the ref (``showSource``)
  // as each swap starts and on every rollback.
  const [gridSource, setGridSource] = useState(() => gridSourceRef.current);

  // "Effect Event" pattern: reads the latest mode and swap handle, while
  // the effect below runs once per payload.
  const hydrateIntakeCacheRef = useRef<
    (data: ForecastGridDataOutput, first: boolean) => void
  >(() => {});
  hydrateIntakeCacheRef.current = (data, first) => {
    void hydrateIntakeCaches(
      data,
      // A first payload opens on its own source, whatever the control holds.
      first ? data.main_intake_source : tableControls.intake,
      { caches: intakeCacheRef, gridSource: gridSourceRef, setGridSource },
      gridRef.current?.api,
      swap
    );
    setAvailableIntakeSources(availableSources(intakeCacheRef.current));
  };

  useEffect(() => {
    if (!forecastData || hydratedForRef.current === forecastData) return;
    const first = hydratedForRef.current === null;
    hydratedForRef.current = forecastData;
    hydrateIntakeCacheRef.current(forecastData, first);
  }, [forecastData, hydratedForRef]);

  // A toggle is running; one queued behind it starts as it settles.
  const toggleRunningRef = useRef(false);
  const handleIntakeToggle = useCallback(
    async (newIntakeMode: IntakeSource) => {
      toggleRunningRef.current = true;
      try {
        // Yield to the browser before the synchronous grid work below.
        // The effect that triggered us already set ``isToggling`` to
        // true; this await lets React commit that re-render and the
        // browser paint the disabled tab + spinner before we freeze
        // the main thread with extract/inject/mutate.
        await paintYield();

        const api = gridRef.current?.api;
        if (!api) return;
        await toggleGridIntake(
          api,
          newIntakeMode,
          hydratedForRef.current?.main_intake_source ?? INITIAL_INTAKE_SOURCE,
          { caches: intakeCacheRef, gridSource: gridSourceRef, setGridSource },
          swap
        );
      } finally {
        toggleRunningRef.current = false;
        setAvailableIntakeSources(availableSources(intakeCacheRef.current));
        setIsToggling(false);
      }
    },
    [gridRef, intakeCacheRef, gridSourceRef, hydratedForRef, swap]
  );

  // Serialise rapid toggles — the same pattern as the cell-edit queue
  // (see ``useWorkspaceMutationQueue``). A Confirmed → Planned →
  // Confirmed burst dispatches only the latest target after the
  // in-flight recalc resolves.
  useIntakeToggleEffect(tableControls.intake, gridSourceRef, toggleRunningRef, {
    trigger: useSerialAsync(handleIntakeToggle),
    setIsToggling
  });

  return {
    intakePending: recompute.isPending || isToggling,
    intakeCacheRef,
    availableIntakeSources,
    gridSource
  };
}
